import React, { memo, useEffect, useState } from "react";
import SlideOutSidebar from "../../../../ui-components/slide-out-sidebar/slideOutSidebar";
import Button, { ButtonThemes } from "../../../../ui-components/button/button";
import { useDispatch, useSelector } from "react-redux";
import { TransactionMessageReprocessState } from "../../../../slices/transaction-monitor/transaction-messages/TransactionMessagesState";
import { submitMessageForReprocessing, transactionMessageReprocessSelector } from "../../../../slices/transaction-monitor/transaction-messages/transactionMessageReprocessSlice";
import { toast } from "react-toastify";
import { Form, Formik, FormikProps } from "formik";
import XGSFormInput from "../../../../ui-components/form/input/xgsFormInput";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import { XGSSelectOption } from "../../../../ui-components/xgs-select/xgsSelect";
import LabeledSelectInput from "../../../../ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { TransactionMessageReprocessData, TransactionMessageReprocessRequest, TransactionMessagesReprocessRequestSchema } from "../../../../app/data/transaction-monitor/models";
import TransactionDefinitionsState from "../../../../slices/transaction-monitor/transaction-definitions/TransactionDefinitionsState";
import { getTransactionDefinitions, transactionDefinitionsSelector } from "../../../../slices/transaction-monitor/transaction-definitions/transactionDefinitionsSlice";
import "../../transactionMonitor.scss";

export interface TransactionMessageSubmitReprocessProps {
    show: boolean,
    onClose: () => void;
}

const initialFormValues = {
    reference: "",
    scopeId: "",
    messageTypeId: "",
}

const TransactionMessageSubmitReprocess: React.FC<TransactionMessageSubmitReprocessProps> = memo((props) => {
    const dispatch = useDispatch();
    const [reference, setReference] = useState<string | null | undefined>(null);
    const [scope, setScope] = useState<XGSSelectOption | null | undefined>(null);
    const [type, setType] = useState<XGSSelectOption | null | undefined>(null);

    const transactionDefinitionsState: TransactionDefinitionsState = useSelector(transactionDefinitionsSelector);
    const transactionMessageReprocessState: TransactionMessageReprocessState = useSelector(transactionMessageReprocessSelector);

    const formRef = React.useRef<FormikProps<TransactionMessageReprocessData>>(null);

    useEffect(() => {
        if (!transactionDefinitionsState.loaded && !transactionDefinitionsState.loading) {
            dispatch(getTransactionDefinitions());
        }
    }, [dispatch, transactionDefinitionsState])

    const getScopeOptions = () => {
        return (
          transactionDefinitionsState.scopes?.map(
            (scope): XGSSelectOption => ({
              label: scope.name,
              value: scope.id,
            })
          ) || []
        );
      };
    
      const getTypeOptions = () => {
        return (
          transactionDefinitionsState.types?.map(
            (type): XGSSelectOption => ({
              label: type.name,
              value: type.id,
            })
          ) || []
        );
      };
    
    const onSubmit = (data: TransactionMessageReprocessData) => {
        let requestData: TransactionMessageReprocessRequest = {
            scopeId: data.scopeId,
            messageTypeId: data.messageTypeId,
            references: [{
                reference: data.reference,
                referenceTypeText: "Custom ID",
                isPrimary: false,
                id: data.reference
            }]
        };
        dispatch(submitMessageForReprocessing(
            requestData,
            () => { toast.info("Message has been submitted for reprocessing successfully!") },
            (error) => { toast.error(error) },
            () => { props.onClose() }
        ));
    };

    return (
        <SlideOutSidebar
            header={"Reprocess Message"}
            show={props.show}
            size="small"
            onClose={props.onClose}
        >
            <div className="xgs-transaction-monitor__submit-reprocess">
                <Formik
                    initialValues={initialFormValues}
                    innerRef={formRef}
                    onSubmit={onSubmit}
                    validationSchema={TransactionMessagesReprocessRequestSchema}
                    enableReinitialize
                >
                    {(props: FormikProps<TransactionMessageReprocessData>) => (
                        <Form className="reprocess-info-form">
                            <XGSFormInput
                                type="text"
                                name="reference"
                                label="Reference"
                                placeholder="Ex: Probill Number, Shipmer ID, Receipt ID, etc."
                                labelMode={LabelModes.column}
                                className="reprocess-info-form__field"
                                value={(reference) ? `${reference}` : undefined}
                                onChange={(e) => {
                                    props.setFieldValue("reference", e?.currentTarget?.value || undefined);
                                    setReference(e?.currentTarget?.value);
                                }
                                }
                            />
                            <LabeledSelectInput
                                onValueChange={(value) => {
                                    props.setFieldValue("scopeId", value?.value);
                                    setScope(value);
                                }}
                                value={scope}
                                options={getScopeOptions()}
                                label="Scope"
                                labelMode={LabelModes.column}
                                className="reprocess-info-form__field"
                                isClearable
                            />
                            <LabeledSelectInput
                                onValueChange={(value) => {
                                    props.setFieldValue("messageTypeId", value?.value);
                                    setType(value);
                                }}
                                value={type}
                                options={getTypeOptions()}
                                label="Message Type"
                                labelMode={LabelModes.column}
                                className="reprocess-info-form__field"
                                isClearable
                            />
                            <Button
                                type="submit"
                                theme={ButtonThemes.blue}
                                spinner={transactionMessageReprocessState.loading}
                                className="reprocess-info-form__btn"
                                disabled={transactionMessageReprocessState.loading}
                            >
                                Submit 
                            </Button>
                        </Form>
                    )}
                </Formik>
            </div>
        </SlideOutSidebar >
    );
});

export default TransactionMessageSubmitReprocess;
